import React, { useState, useRef } from 'react'
import './ImportButton.css'
import UploadIcon from '@mui/icons-material/Upload'
import CloseIcon from '@mui/icons-material/Close'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import CircularProgress from '@mui/material/CircularProgress'
import * as XLSX from 'xlsx'
import httpClient from '../helpers/httpClient'

function ImportButton({ 
  onImportSuccess,
  onImportError,
  endpoint,
  acceptedTypes = ['.xlsx', '.xls'],
  disabled = false,
  className = '',
  children = 'Import',
  maxFileSize = 10 * 1024 * 1024, // 10MB default
  additionalData = {} // Additional data to send with the file
}) {
  const [showPopup, setShowPopup] = useState(false)
  const [loading, setLoading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)
  const fileInputRef = useRef(null)

  const isExcelFile = (file) => {
    const excelTypes = [
      'application/vnd.openxml.formats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      '.xlsx',
      '.xls'
    ]
    return excelTypes.some(type => 
      file.type === type || file.name.toLowerCase().endsWith(type)
    )
  }

  const validateFile = (file) => {
    if (!file) {
      throw new Error('No file selected')
    }

    if (file.size > maxFileSize) {
      throw new Error(`File size exceeds ${Math.round(maxFileSize / (1024 * 1024))}MB limit`)
    }

    if (!isExcelFile(file)) {
      throw new Error('Please select an Excel file (.xlsx or .xls)')
    }

    return true
  }

  const parseExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result)
          const workbook = XLSX.read(data, { type: 'array' })
          
          // Get the first sheet
          const firstSheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[firstSheetName]
          
          // Convert to JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 })
          
          resolve({
            data: jsonData,
            sheetName: firstSheetName,
            allSheetNames: workbook.SheetNames
          })
        } catch (error) {
          reject(new Error('Failed to parse Excel file: ' + error.message))
        }
      }
      
      reader.onerror = () => {
        reject(new Error('Failed to read file'))
      }
      
      reader.readAsArrayBuffer(file)
    })
  }

  const handleFileSelect = (file) => {
    try {
      validateFile(file)
      setSelectedFile(file)
    } catch (error) {
      if (onImportError) {
        onImportError(error.message)
      } else {
        alert(error.message)
      }
    }
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setDragOver(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setDragOver(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    
    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      handleFileSelect(files[0])
    }
  }

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files)
    if (files.length > 0) {
      handleFileSelect(files[0])
    }
  }

  const handleBrowseClick = () => {
    fileInputRef.current?.click()
  }

  const handleImport = async () => {
    if (!selectedFile || !endpoint) return

    setLoading(true)

    try {
      // Parse the Excel file
      const excelData = await parseExcelFile(selectedFile)
      
      // Prepare the data to send to the backend
      const importData = {
        filename: selectedFile.name,
        data: excelData.data,
        sheetName: excelData.sheetName,
        allSheetNames: excelData.allSheetNames,
        fileSize: selectedFile.size,
        ...additionalData
      }

      // Send to backend
      const response = await httpClient.post(endpoint, importData)

      if (onImportSuccess) {
        onImportSuccess(response.data, selectedFile)
      }

      // Reset state
      setSelectedFile(null)
      setShowPopup(false)
      
    } catch (error) {
      console.error('Import failed:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Import failed'
      
      if (onImportError) {
        onImportError(errorMessage, error)
      } else {
        alert(errorMessage)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleClosePopup = () => {
    if (!loading) {
      setShowPopup(false)
      setSelectedFile(null)
      setDragOver(false)
    }
  }

  const handleButtonClick = () => {
    if (disabled || loading) return
    setShowPopup(true)
  }

  return (
    <>
      <button 
        className={`import-button ${className}`}
        onClick={handleButtonClick}
        disabled={disabled || loading}
      >
        {loading ? (
          <CircularProgress size={16} color="inherit" />
        ) : (
          <>
            <UploadIcon style={{ marginRight: '4px', fontSize: '14px' }} />
            {children}
          </>
        )}
      </button>

      {showPopup && (
        <div className="import-popup-overlay" onClick={handleClosePopup}>
          <div className="import-popup" onClick={(e) => e.stopPropagation()}>
            <div className="import-popup-header">
              <h3>Import Excel File</h3>
              <button 
                className="import-popup-close"
                onClick={handleClosePopup}
                disabled={loading}
              >
                <CloseIcon />
              </button>
            </div>

            <div className="import-popup-content">
              {!selectedFile ? (
                <div 
                  className={`file-drop-zone ${dragOver ? 'drag-over' : ''}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <CloudUploadIcon className="drop-icon" />
                  <p>Drop your Excel file here</p>
                  <span>or</span>
                  <button 
                    className="browse-button"
                    onClick={handleBrowseClick}
                    type="button"
                  >
                    <FolderOpenIcon style={{ marginRight: '8px' }} />
                    Browse Files
                  </button>
                  <small>
                    Accepted formats: {acceptedTypes.join(', ')} 
                    <br />
                    Max size: {Math.round(maxFileSize / (1024 * 1024))}MB
                  </small>
                </div>
              ) : (
                <div className="file-selected">
                  <div className="file-info">
                    <h4>Selected File:</h4>
                    <p>{selectedFile.name}</p>
                    <small>Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB</small>
                  </div>
                  
                  <div className="import-actions">
                    <button 
                      className="cancel-button"
                      onClick={() => setSelectedFile(null)}
                      disabled={loading}
                    >
                      Choose Different File
                    </button>
                    <button 
                      className="import-confirm-button"
                      onClick={handleImport}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <CircularProgress size={16} color="inherit" style={{ marginRight: '8px' }} />
                          Importing...
                        </>
                      ) : (
                        'Import File'
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedTypes.join(',')}
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
      />
    </>
  )
}

export default ImportButton 